import { FC, useState } from 'react';
import { UserPlus } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@new/common/ui';
import {
  useGetCourseInstructorsQuery,
  useGetCourseQuery,
  useSetMainInstructorMutation,
} from '@teachur/http-client';
import type { User as UserType } from '@teachur/http-client';
import { InstructorList } from './InstructorList';
import { InstructorSearch } from './InstructorSearch';
import { AddInstructorModal } from './AddInstructorModal';
import { DeleteInstructorModal } from './DeleteInstructorModal';

interface CourseInstructorsTabProps {
  courseId: number;
}

export const CourseInstructorsTab: FC<CourseInstructorsTabProps> = ({ courseId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteInstructor, setDeleteInstructor] = useState<UserType | null>(null);

  const { data: instructors, isLoading } = useGetCourseInstructorsQuery(courseId, {
    staleTime: 5 * 60 * 1000,
  });
  const { data: course } = useGetCourseQuery(courseId);
  const { mutate: setMainInstructor, isPending: isSettingMainInstructor } =
    useSetMainInstructorMutation({
      onSuccess: () => toast.success('Main instructor updated!'),
      onError: () => toast.error('Failed to update main instructor.'),
    });

  const mainInstructorId = course?.mainInstructor?.id;

  const filteredInstructors =
    instructors?.filter((instructor: UserType) => {
      const fullName = `${instructor.firstName} ${instructor.lastName}`.toLowerCase();
      const email = instructor.email?.toLowerCase() || '';
      const query = searchQuery.toLowerCase();
      return fullName.includes(query) || email.includes(query);
    }) || [];

  const onDeleteInstructor = (instructor: UserType) => {
    setDeleteInstructor(instructor);
  };

  const onSetMainInstructor = (instructor: UserType) => {
    setMainInstructor({
      courseId,
      userId: instructor.id,
      addRemove: mainInstructorId !== instructor.id,
    });
  };

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      <h3 className="[font-size:var(--fs-h6)]! lg:[font-size:var(--fs-h5)]! text-(--neutral-black-NB1000)! font-bold m-0!">
        Instructors
      </h3>

      <div className="flex items-center gap-4">
        <InstructorSearch searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        <Button variant="primary" onClick={() => setIsAddModalOpen(true)} className="gap-2">
          <UserPlus size={16} />
          <span className="hidden sm:inline">Add Instructor</span>
        </Button>
      </div>

      <InstructorList
        instructors={filteredInstructors}
        isLoading={isLoading}
        searchQuery={searchQuery}
        mainInstructorId={mainInstructorId}
        onDelete={onDeleteInstructor}
        onSetMainInstructor={onSetMainInstructor}
        isDeleting={false}
        isSettingMainInstructor={isSettingMainInstructor}
      />

      <AddInstructorModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        courseId={courseId}
      />

      {deleteInstructor && (
        <DeleteInstructorModal
          isOpen={!!deleteInstructor}
          onClose={() => setDeleteInstructor(null)}
          courseId={courseId}
          instructor={deleteInstructor}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
};
